import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  WritableSignal,
  input,
  signal,
  InputSignal,
} from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import {
  AnalyticsInfo,
  AnalyticsRangeContext,
  ChartType,
  getChangePercentage,
  getComparePeriodLabel,
  getDefaultComparePeriodForRange,
  getStatsSum,
} from '@services/analytics-services/core';
import { BadgesComponent } from '@enginuity/core/molecules/badges/badges.component';
import { FormattingService } from '@services/analytics-services/core/formatting.service';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { formatHour } from '@services/core-services/utils/date.utils';

type Formatter = (value: number) => string;

@Component({
  selector: 'app-analytics-trend-widget',
  imports: [BadgesComponent, HighchartsChartModule],
  templateUrl: './analytics-trend-widget.component.html',
  styleUrl: './analytics-trend-widget.component.scss',
  providers: [DatePipe, DecimalPipe, CurrencyPipe, FormattingService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalyticsTrendWidgetComponent implements OnChanges {
  @Input() metricsKey: string = 'viewed';
  @Input() title: string = '';
  @Input() height: any = 'auto';
  @Input() width: number = 100;
  @Input() analytics: AnalyticsInfo<any> | undefined | null = undefined;
  @Input({ required: true }) context: AnalyticsRangeContext | undefined;
  @Input() type: ChartType = 'line';
  @Input() formatter: 'number' | 'currency' | Formatter | undefined = 'number';
  errorState: InputSignal<string | false> = input<string | false>(false);

  protected readonly isNaN = isNaN;
  protected readonly actions: any = [];

  protected comparePeriodLabel: string = '';
  protected changeValue: number | undefined = undefined;
  protected value: number | undefined = undefined;
  protected formattedValue: string | undefined | null = undefined;
  protected readonly Highcharts = Highcharts;

  protected chartOptions: WritableSignal<any> = signal({
    chart: {
      type: 'spline',
      height: 60,
      animation: {
        duration: 500,
      },
    },
    subtitle: null,
    title: {
      text: null,
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      visible: false,
      type: 'datetime',
    },
    yAxis: {
      visible: false,
    },
    plotOptions: {
      spline: {
        marker: {
          enabled: false,
        },
        lineWidth: 1.6,
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      crosshairs: true,
      useHTML: true,
      backgroundColor: 'rgba(35, 35, 35, 0.95)',
      borderWidth: 0,
      borderRadius: 8,
      shadow: true,
      outside: true,
      style: {
        color: '#ffffff',
        fontSize: '12px',
        fontWeight: 'normal',
      },
      formatter: function (this: any): string {
        try {
          const component = this.series.chart.options.component;
          const chartTitle = component?.name || this.series.name || 'Data';
          const context = this.series.chart.options.context;
          let dateStr = '';

          if (context?.interval === 'hour') {
            const date = new Date(this.x);
            const currentHour = date.getHours();
            const nextHour = currentHour + 1;
            const dayOfMonth = date.getDate();
            const monthShort = date.toLocaleString('en-US', { month: 'short' });
            dateStr = `${dayOfMonth} ${monthShort} ${formatHour(currentHour)} - ${formatHour(nextHour)}`;
          } else if (context?.interval === 'week') {
            const date = new Date(this.x);
            const dayOfMonth = date.getDate();
            const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
            const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust for Sunday
            const diffToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;

            const startOfWeek = new Date(date);
            startOfWeek.setDate(dayOfMonth + diffToMonday);
            const startDay = startOfWeek.getDate();
            const startMonth = startOfWeek.toLocaleString('en-US', { month: 'short' });

            const endOfWeek = new Date(date);
            endOfWeek.setDate(dayOfMonth + diffToSunday);
            const endDay = endOfWeek.getDate();
            const endMonth = endOfWeek.toLocaleString('en-US', { month: 'short' });
            const endYear = endOfWeek.getFullYear();

            if (startMonth === endMonth) {
              dateStr = `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`;
            } else {
              dateStr = `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`;
            }
          } else if (context?.interval === 'month') {
            const date = new Date(this.x);
            const monthShort = date.toLocaleString('en-US', { month: 'short' });
            const year = date.getFullYear();
            dateStr = `${monthShort} ${year}`;
          } else if (context?.interval === 'year') {
            const date = new Date(this.x);
            const year = date.getFullYear();
            dateStr = `${year}`;
          } else {
            // Default to Day format (handles 'day' or unknown interval)
            const date = new Date(this.x);
            const dayOfMonth = date.getDate();
            const monthShort = date.toLocaleString('en-US', { month: 'short' });
            const year = date.getFullYear();
            dateStr = `${dayOfMonth} ${monthShort} ${year}`;
          }

          // Format the value based on component options
          let formattedValue = '';
          if (this.y !== undefined) {
            if (component?.formatValue) {
              formattedValue = component.formatValue(this.y);
            } else if (component?.formatter === 'currency') {
              formattedValue = '₹' + this.y.toLocaleString();
            } else {
              formattedValue = this.y.toLocaleString();
            }
          } else {
            formattedValue = '0';
          }

          return `
          <div class="custom-chart-tooltip" style="padding:12px;">
            <div class="d-flex flex-column spacing-8 align-items-start w-100">
              <h4 class="h4-bold primary-0 text-capitalize" style="margin: 0; color: white;">${dateStr}</h4>
              <span class="dash-border w-100"></span>
              <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                <div class="d-flex align-items-center spacing-4 w-100 text-center">
                  <div style="background:${this.color || this.series.color}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                  <div class="h4-reg primary-50">${chartTitle}</div>
                </div>
                <div class="h4-bold primary-0">${formattedValue}</div>
              </div>
            </div>
          </div>
          `;
        } catch (error) {
          console.error('Tooltip error:', error);
          return 'Error displaying tooltip';
        }
      },
    },

    series: [
      {
        marker: { enabled: false },
        color: 'var(--color-green)',
        lineWidth: 1.6,
        data: [],
      },
    ],

    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
          },
        },
      ],
    },
  });

  get changeType(): 'tag' | 'cancelled' | 'completed' {
    if (!this.changeValue) return 'tag';
    return this.changeValue > 0 ? 'completed' : 'cancelled';
  }

  get iconType(): 'caret_up' | 'caret_down' | 'caret_right' {
    if (!this.changeValue) return 'caret_right';
    return this.changeValue > 0 ? 'caret_up' : 'caret_down';
  }

  constructor(private readonly formattingService: FormattingService) {}

  ngOnChanges(changes: SimpleChanges) {
    // Get the current options value from the signal
    let currentOptions = this.chartOptions();
    let optionsChanged = false;

    // Handle Context Changes
    if (changes['context'] && this.context) {
      this.comparePeriodLabel = getComparePeriodLabel(
        getDefaultComparePeriodForRange(this.context.preset)
      );
      // Prepare context update for the new options object
      currentOptions = { ...currentOptions, context: this.context };
      optionsChanged = true;
    }

    // Handle Analytics Changes
    if (changes['analytics']) {
      const analytics = changes['analytics'].currentValue as AnalyticsInfo<any>;

      if (analytics && this.context) {
        // Ensure context is available for calculations
        const { stats, compare_stats } = analytics;

        // Update component values
        this.changeValue = getChangePercentage(stats, compare_stats, this.metricsKey);
        this.value = getStatsSum(stats, this.metricsKey);
        this.formattedValue = this.formatValue(this.value, this.formatter);

        // Prepare data for chart options
        const componentReference = {
          name: this.title,
          value: this.value,
          formatter: this.formatter,
          formatValue: (val: number) => this.formatValue(val, this.formatter),
        };

        const chartData = stats?.map(info => {
          const date = new Date(info.date);
          return {
            x: date.getTime(),
            y: info.metrics[this.metricsKey] ?? 0,
          };
        });

        // Update relevant chart options
        const newSeries = {
          name: this.title,
          data: chartData,
          color: 'var(--color-green)',
        };

        // Merge analytics changes into the options object
        currentOptions = {
          ...currentOptions,
          chart: {
            ...currentOptions.chart,
            type: 'spline',
          },
          component: componentReference,
          xAxis: {
            type: 'datetime',
            visible: false,
          },
          series: [newSeries],
        };
        optionsChanged = true;
      } else {
        // Analytics is null/undefined, potentially clear chart?
        // Or handle as needed, maybe clear component values
        this.changeValue = undefined;
        this.value = undefined;
        this.formattedValue = undefined;
        currentOptions = {
          ...currentOptions,
          series: [{ ...currentOptions.series[0], data: [] }],
        } as WritableSignal<any>;
        optionsChanged = true;
      }
    }

    // Apply updated options if anything changed
    if (optionsChanged) {
      this.chartOptions.set(currentOptions);
    }
  }

  private formatValue(value: number, formatter: 'number' | 'currency' | Formatter | undefined) {
    return this.formattingService.formatValue(value, formatter);
  }
}
